import React from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import SectionHelp from './SectionHelp';
import '../styles/DimensionsSection.css';

const DimensionsSection = () => {
  const {
    dimensionsRows,
    updateDimensionsRow,
    cookieRows,
    showCalculationFields,
    setShowCalculationFields,
    isMetric,
    isMetricWeight,
    sortDimensionsRows,
    isMobile,
    setCurrentAnalyzingRow,
    setShowImageAnalyzer,
    goToNextSection,
    activeSection
  } = useProCalculator();

  return (
    <div className="dimensions-section">
      {/* Header with title only */}
      <div className="section-header">
        <h2 className="section-title">Cookie Dimensions</h2>
      </div>

      {/* Help text container */}
      <div className="section-help-container">
        <SectionHelp sectionId="dimensions" isMobile={isMobile} />
      </div>

      {/* Actions container for buttons */}
      <div className="section-actions-container">
        <button 
          onClick={sortDimensionsRows}
          className="sort-cookies-button"
          title="Sort cookies alphabetically"
        >
          Sort Cookies
        </button>
        
        {!isMobile && (
          <button
            onClick={() => setShowCalculationFields(!showCalculationFields)}
            className={`calculation-toggle-button ${showCalculationFields ? 'active' : ''}`}
            title="Toggle calculation fields"
          >
            {showCalculationFields ? 'Hide Calculations' : 'Show Calculations'}
          </button>
        )}
      </div>
      
      <div className={`dimensions-table ${showCalculationFields ? 'show-calculations' : ''}`}>
        {!isMobile && (
          <div className={`dimensions-table-header ${showCalculationFields ? 'show-calculations' : ''}`}>
            <div className="cookie-name-header">Cookie Name</div>
            <div className="shape-type-header">Shape Type</div>
            <div className="dimensions-header">Dimensions</div>
            {showCalculationFields && (
              <>
                <div className="area-header">Area ({isMetric ? 'cm²' : 'in²'})</div>
                <div className="icing-header">Icing (g)</div>
              </>
            )}
          </div>
        )}
        
        {dimensionsRows.map(row => {
          // Find the corresponding cookie name
          const cookie = cookieRows.find(c => c.id === row.cookieId);
          const cookieName = cookie ? cookie.cookieName : '---';
          
          return (
            <div key={row.id} className={`dimensions-row ${showCalculationFields ? 'show-calculations' : ''}`}>
              <div className="cookie-name-field">
                {cookieName}
              </div>
              
              <div className="shape-type-field">
                <select
                  value={row.shapeType}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateDimensionsRow(row.id, 'shapeType', value);
                    
                    // Clear dimensions when changing shape type
                    if (value === 'circle') {
                      updateDimensionsRow(row.id, 'width', '');
                      updateDimensionsRow(row.id, 'length', '');
                    } else if (value === 'square') {
                      updateDimensionsRow(row.id, 'diameter', '');
                      updateDimensionsRow(row.id, 'length', '');
                    } else {
                      updateDimensionsRow(row.id, 'diameter', '');
                    }
                  }}
                  className="shape-type-select"
                >
                  <option value="">Select shape</option>
                  <option value="rectangle">Rectangle</option>
                  <option value="square">Square</option>
                  <option value="circle">Circle</option>
                  {/* <option value="triangle">Triangle</option> */}
                  {/* <option value="custom">Custom (Image Analysis)</option> */}
                </select>
              </div>
              
              <div className="dimensions-fields">
                {row.shapeType === 'square' && (
                  <div className="dimension-field">
                    <label>Side Length:</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.width}
                      onChange={(e) => updateDimensionsRow(row.id, 'width', e.target.value)}
                      className="dimension-input"
                    />
                    <span className="unit-label">{isMetric ? 'mm' : 'in'}</span>
                  </div>
                )}
                
                {(row.shapeType === 'rectangle' || row.shapeType === 'triangle' || row.shapeType === 'custom') && (
                  <>
                    <div className="dimension-field">
                      <label>{row.shapeType === 'triangle' ? 'Max Width:' : row.shapeType === 'custom' ? 'Max Width:' : 'Width:'}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.width}
                        onChange={(e) => updateDimensionsRow(row.id, 'width', e.target.value)}
                        className="dimension-input"
                      />
                      <span className="unit-label">{isMetric ? 'mm' : 'in'}</span>
                    </div>
                    
                    <div className="dimension-field">
                      <label>{row.shapeType === 'custom' ? 'Max Length:' : 'Length:'}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.length}
                        onChange={(e) => updateDimensionsRow(row.id, 'length', e.target.value)}
                        className="dimension-input"
                      />
                      <span className="unit-label">{isMetric ? 'mm' : 'in'}</span>
                    </div>
                  </>
                )}
                
                {row.shapeType === 'custom' && (
                  <div className="analyze-button-container">
                    <button 
                      onClick={() => {
                        setCurrentAnalyzingRow(row);
                        setShowImageAnalyzer(true);
                      }}
                      className="analyze-button"
                    >
                      Analyze Image
                    </button>
                  </div>
                )}
                
                {row.shapeType === 'circle' && (
                  <div className="dimension-field">
                    <label>Diameter:</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.diameter}
                      onChange={(e) => updateDimensionsRow(row.id, 'diameter', e.target.value)}
                      className="dimension-input"
                    />
                    <span className="unit-label">{isMetric ? 'mm' : 'in'}</span>
                  </div>
                )}
                
              </div>
              {showCalculationFields && row.shapeType && (
                <div className="calculation-field area-field"> 
                  <label className="mobile-only-label">Area:</label>
                  <span className="calculation-value">{(row.area || 0).toFixed(4)} {isMetric ? 'cm²' : 'in²'}</span>
                </div>
              )}
              {showCalculationFields && row.shapeType && (
                <div className="calculation-field icing-field"> 
                  <label className="mobile-only-label">Icing:</label>
                  <span className="calculation-value">{(row.icingNeeded || 0).toFixed(2)} g</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Next section button */}
      {activeSection !== 'all' && (
        <button
          className="next-section-button"
          onClick={() => goToNextSection('dimensions')}
        >
          Next: Color %
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5l7 7-7 7"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

export default DimensionsSection;
